import OrderUtils from 'utils/Order';
import checkoutApi from 'services/api/checkout';
import checkoutUtils from 'utils/Checkout';
import OrderActions from 'actions/OrderActions';
import LoadScripts from 'utils/LoadScripts';
import localeUtils from 'utils/LanguageLocale';
import GooglePayEN from 'utils/locales/GooglePay_en_US.js';
import GooglePayFR from 'utils/locales/GooglePay_fr_CA.js';

// Required by the official Google Pay spec
// (https://developers.google.com/pay/api/web/guides/tutorial)
const BASE_REQUEST = {
    apiVersion: 2,
    apiVersionMinor: 0
};

const ALLOWED_CARD_AUTH_METHODS = ['PAN_ONLY', 'CRYPTOGRAM_3DS'];

const ALLOWED_CARD_NETWORKS = ['VISA', 'MASTERCARD', 'AMEX', 'DISCOVER'];

const GOOGLE_PAY_ERROR_MESSAGES = {
    incompleteCheckout: 'incompleteCheckout'
};

const SDK_LOAD_TIMEOUT_MS = 10000;

const TYPES = {
    ENABLED: 'ENABLED',
    HIDDEN: 'HIDDEN'
};

// Holds data for the current checkout (order, shippingGroup, shippingMethods, etc) so future callbacks
// don't have to refetch it
let checkoutContext = null;

/**
 * The two-letter country codes Google Pay should accept for the shipping address.
 *
 * @returns {string[]}
 */
function getAllowedCountryCodes() {
    return [localeUtils.isCanada() ? 'CA' : 'US'];
}

/**
 * Resolves the Ship-to-Home (HardGood) shipping group from a refreshed order.
 *
 * @param {Object} orderDetails
 * @returns {{ shippingGroupId: (string|number), shippingMethod: Object }}
 */
function getShippingGroupFromOrderDetails(orderDetails) {
    const entries = orderDetails?.shippingGroups?.shippingGroupsEntries || [];
    const entry = entries.find(item => item.shippingGroupType === OrderActions.SHIPPING_GROUPS.HARDGOOD);

    return entry.shippingGroup;
}

/**
 * Maps the Cart Service available shipping methods to Google Pay's
 * `shippingOptionParameters`. Google's option has no amount field, so the fee
 * is appended to the description
 *
 * @param {Array} shippingMethods - from checkoutApi.getAvailableShippingMethods
 * @param {string|number} [currentShippingMethodId] - the order's current method, used as default
 * @returns {{ defaultSelectedOptionId: string, shippingOptions: Array<{id,label,description}> }}
 */
function getShippingOptions(shippingMethods = [], currentShippingMethodId) {
    const shippingOptions = shippingMethods.map(method => ({
        id: String(method.shippingMethodId),
        label: method.shippingMethodType,
        description: [method.shippingMethodDescription, checkoutUtils.setShippingFee(method.shippingFee)].filter(Boolean).join(' — ')
    }));

    const currentId = String(currentShippingMethodId);
    const defaultSelectedOptionId = shippingOptions.some(option => option.id === currentId) ? currentId : shippingOptions[0]?.id;

    return { defaultSelectedOptionId, shippingOptions };
}

function getBaseCardPaymentMethod() {
    return {
        type: 'CARD',
        parameters: {
            allowedAuthMethods: ALLOWED_CARD_AUTH_METHODS,
            allowedCardNetworks: ALLOWED_CARD_NETWORKS
        }
    };
}

function getTokenizationSpecification() {
    const { googlePayGateway, gatewayMerchantIdUS, gatewayMerchantIdCA } = Sephora.configurationSettings.googlePayConfig || {};
    const gatewayMerchantId = localeUtils.isCanada() ? gatewayMerchantIdCA : gatewayMerchantIdUS;

    return {
        type: 'PAYMENT_GATEWAY',
        parameters: {
            gateway: googlePayGateway,
            gatewayMerchantId
        }
    };
}

function getCardPaymentMethod() {
    const baseCardPaymentMethod = getBaseCardPaymentMethod();

    return {
        ...baseCardPaymentMethod,
        parameters: {
            ...baseCardPaymentMethod.parameters,
            // Returns the billing address tied to the selected card
            billingAddressRequired: true,
            billingAddressParameters: {
                format: 'FULL',
                phoneNumberRequired: true
            }
        },
        tokenizationSpecification: getTokenizationSpecification()
    };
}

function getIsReadyToPayRequest() {
    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getBaseCardPaymentMethod()]
    };
}

function getMerchantInfo() {
    const { merchantId, merchantName } = Sephora.configurationSettings.googlePayConfig || {};

    return {
        merchantId,
        merchantName
    };
}

function getTransactionInfo({ transactionAmount, transactionCurrencyCode }) {
    // Resolve EN/FR at call time (not module load) so the label follows the
    // customer's current locale — same pattern as utils/Checkout setShippingFee.
    const getText = localeUtils.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

    return {
        countryCode: localeUtils.isCanada() ? 'CA' : 'US',
        currencyCode: transactionCurrencyCode,
        totalPriceStatus: 'ESTIMATED',
        totalPrice: String(transactionAmount),
        totalPriceLabel: getText('estimatedTotal')
    };
}

function getPaymentDataRequest({ transactionAmount, transactionCurrencyCode, shippingMethods, currentShippingMethodId }) {
    return {
        ...BASE_REQUEST,
        allowedPaymentMethods: [getCardPaymentMethod()],
        transactionInfo: getTransactionInfo({ transactionAmount, transactionCurrencyCode }),
        merchantInfo: getMerchantInfo(),
        emailRequired: true,
        shippingAddressRequired: true,
        shippingAddressParameters: {
            phoneNumberRequired: true,
            allowedCountryCodes: getAllowedCountryCodes()
        },
        shippingOptionRequired: true,
        shippingOptionParameters: getShippingOptions(shippingMethods, currentShippingMethodId)
    };
}

const GooglePayUtils = {
    TYPES,

    paymentsClient: null,

    /**
     * Whether Google Pay should be offered to the current user, based on the
     * locale-specific killswitches in `googlePayConfig`.
     *
     * @returns {boolean}
     */
    isGooglePayEnabled: () => {
        const { isGooglePayDotComEnabledUS, isGooglePayDotComEnabledCA } = Sephora.configurationSettings.googlePayConfig || {};

        if (localeUtils.isUS()) {
            return Boolean(isGooglePayDotComEnabledUS?.isEnabled);
        } else if (localeUtils.isCanada()) {
            return Boolean(isGooglePayDotComEnabledCA?.isEnabled);
        }

        return false;
    },

    /**
     * Resolves the Google Pay button visibility state. Returns HIDDEN when
     * the locale killswitch is off OR the device/browser is not eligible
     * for Google Pay; ENABLED otherwise. Errors during SDK load or the
     * isReadyToPay probe are treated as HIDDEN.
     *
     * @returns {Promise<'ENABLED' | 'HIDDEN'>}
     */
    getGooglePaymentType: async function () {
        if (!this.isGooglePayEnabled()) {
            return TYPES.HIDDEN;
        }

        try {
            await this.initPaymentsClient();

            return TYPES.ENABLED;
        } catch (err) {
            return TYPES.HIDDEN;
        }
    },

    /**
     * Singleton for google.payments.api.PaymentsClient. Created on first call;
     * subsequent calls return the same instance.
     *
     * environment: 'TEST' | 'PRODUCTION' (official SDK values).
     *
     * @throws {Error} when the SDK script has not been loaded yet.
     * @returns {google.payments.api.PaymentsClient}
     */
    getPaymentsClient: function () {
        if (!this.paymentsClient) {
            const PaymentsClient = window.google?.payments?.api?.PaymentsClient;

            if (!PaymentsClient) {
                throw new Error('Google Pay SDK not loaded');
            }

            const { googlePayEnvironment } = Sephora.configurationSettings.googlePayConfig || {};

            // TODO GOOGLE:  The onPaymentDataChanged/onPaymentAuthorized handlers below are still placeholders
            // (PAY-6706/6707): wiring them now would break the paysheet
            this.paymentsClient = new PaymentsClient({ environment: googlePayEnvironment });
        }

        return this.paymentsClient;
    },

    /**
     * Creates the Google Pay payment group on the backend (mirrors Paze's
     * initPaymentGroup) and resolves with the refreshed order details.
     *
     * @param {string|number} orderId - the checkout order id resolved from
     *   initializeCheckout
     * @returns {Promise<Object>} resolved order details
     */
    initGooglePaymentGroup: async function (orderId) {
        await checkoutApi.initializePaymentGroup({ paymentGroupType: OrderUtils.PAYMENT_GROUP_TYPE.GOOGLE_PAY });

        return checkoutApi.getOrderDetails(orderId);
    },

    /**
     * Called after the Google Pay SDK script has loaded. Instantiates the
     * PaymentsClient and calls `isReadyToPay` to confirm the device/browser
     * supports Google Pay.
     *
     * @returns {Promise<void>} resolves when Google Pay is confirmed available
     * @throws {Error} if `isReadyToPay` returns a falsy result, indicating
     *   Google Pay is not supported on this device
     */
    onLibraryLoad: async function () {
        const client = this.getPaymentsClient();
        const response = await client.isReadyToPay(getIsReadyToPayRequest());

        if (!response?.result) {
            // TODO: GOOGLE — handle Google Pay unavailable on user's machine
            throw new Error('Google Pay is not available on this device');
        }
    },

    /**
     * Loads the SDK and verifies Google Pay is available on this device.
     * Memoizes the in-flight Promise so concurrent callers (e.g. desktop +
     * mobile ButtonGrid instances) share a single SDK load. On rejection
     * the cache is cleared so it can be retried.
     *
     * @returns {Promise<void>} resolves when the client is ready for
     *   startCheckout; rejects if the SDK can't load or the device is ineligible.
     */
    initPaymentsClient: function () {
        if (!this.initPromise) {
            this.initPromise = (async () => {
                try {
                    await this.loadLibrary();
                    await this.onLibraryLoad();
                } catch (err) {
                    this.initPromise = null;
                    this.loadLibraryPromise = null;
                    this.paymentsClient = null;
                    throw err;
                }
            })();
        }

        return this.initPromise;
    },

    /**
     * Loads the Google Pay JS SDK once (memoized). Rejects if the library
     * URL is missing from config or the script fails to load within
     * SDK_LOAD_TIMEOUT_MS — without these guards the Promise could hang
     * forever (LoadScripts has no onerror handler).
     *
     * @returns {Promise<void>} resolves when the script is loaded
     */
    loadLibrary: function () {
        if (!this.loadLibraryPromise) {
            const url = Sephora.configurationSettings.googlePayConfig?.googlePayLibrary;

            if (!url) {
                this.loadLibraryPromise = Promise.reject(new Error('Google Pay library URL not configured'));
            } else {
                this.loadLibraryPromise = new Promise((resolve, reject) => {
                    const timeoutId = setTimeout(() => reject(new Error('Google Pay SDK load timed out')), SDK_LOAD_TIMEOUT_MS);

                    LoadScripts.loadScripts(
                        [url],
                        () => {
                            clearTimeout(timeoutId);
                            resolve();
                        },
                        true
                    );
                });
            }
        }

        return this.loadLibraryPromise;
    },

    /**
     * STH shipping callback. SCAFFOLDING for PAY-6706
     *
     * @param {Object} intermediatePaymentData
     * @returns {Promise<Object>} PaymentDataRequestUpdate
     */
    // eslint-disable-next-line no-unused-vars
    onPaymentDataChanged: async function (intermediatePaymentData) {
        // TODO: GOOGLE: PAY-6706 → shared handler PAY-6704: validate address, recalculate taxes
        // and shipping, return updated newTransactionInfo + newShippingOptionParameters.
        return {};
    },

    /**
     * STH authorization callback. SCAFFOLDING for PAY-6707
     *
     * @param {Object} paymentData
     * @returns {Promise<{ transactionState: string }>} PaymentAuthorizationResult
     */
    // eslint-disable-next-line no-unused-vars
    onPaymentAuthorized: async function (paymentData) {
        // TODO: GOOGLE: PAY-6707 → shared handler PAY-6705: create shipping address, add the card
        // to the order, place the order, and resolve to the confirmation page.
        return {
            transactionState: 'ERROR',
            error: {
                intent: 'PAYMENT_AUTHORIZATION',
                message: 'Google Pay authorization is not yet implemented',
                reason: 'OTHER_ERROR'
            }
        };
    },

    /**
     * Opens the Google Pay payment sheet with a prebuilt PaymentDataRequest and maps
     * the user-cancel outcome. Must be invoked AFTER `await initPaymentsClient()`.
     *
     * @param {Object} paymentDataRequest
     * @returns {Promise<{ googlePay: { paymentData: Object } }>}
     * @throws {Error} with `reason === 'incompleteCheckout'` when the user cancels.
     */
    requestPaymentData: async function (paymentDataRequest) {
        const googleClient = this.getPaymentsClient();

        try {
            const paymentData = await googleClient.loadPaymentData(paymentDataRequest);

            return { googlePay: { paymentData } };
        } catch (err) {
            // Google Pay returns statusCode: 'CANCELED' when the user closes the sheet
            if (err?.statusCode === 'CANCELED') {
                const error = new Error('User cancelled Google Pay checkout');

                error.reason = GOOGLE_PAY_ERROR_MESSAGES.incompleteCheckout;

                throw error;
            }

            throw err;
        }
    },

    /**
     * Runs the Ship-to-Home express-checkout init sequence, then opens the paysheet:
     *   order/init → paymentGroup (GooglePayPaymentGroup) → getOrderDetails →
     *   getAvailableShippingMethods → build STH PaymentDataRequest → loadPaymentData.
     * Populates `checkoutContext`, used to build the request and reserved for the
     * PAY-6706/6707 callbacks once they are wired in.
     * On any init failure the order-failure flow runs and the sheet is NOT opened.
     *
     * @param {Object} args
     * @param {string} args.transactionAmount - amount in major units (e.g. '10.00')
     * @param {string} args.transactionCurrencyCode - ISO 4217 code (e.g. 'USD', 'CAD')
     * @returns {Promise<{ googlePay: { paymentData: Object } } | undefined>}
     * @throws {Error} with `reason === 'incompleteCheckout'` when the user cancels.
     */
    startCheckout: async function ({ transactionAmount, transactionCurrencyCode }) {
        checkoutContext = null;

        let orderDetails;
        let shippingMethods = [];

        try {
            // Ensure the client exists before loadPaymentData
            this.getPaymentsClient();

            // initializeCheckout resolves with the checkout order; pass it on.
            // On the basket page OrderUtils.getOrderId() (order.orderDetails store slice)
            // is still empty, so reading it here would call getOrderDetails(undefined).
            const order = await checkoutUtils.initializeCheckout({ isGooglePayFlow: true });
            const orderId = order?.orderId || order?.currentOrderId;

            orderDetails = await this.initGooglePaymentGroup(orderId);

            const shippingGroup = getShippingGroupFromOrderDetails(orderDetails);
            const shippingGroupId = shippingGroup.shippingGroupId;
            const currentShippingMethodId = shippingGroup.shippingMethod?.shippingMethodId;

            const response = await checkoutApi.getAvailableShippingMethods(orderId, shippingGroupId);
            shippingMethods = response?.shippingMethods || [];

            checkoutContext = {
                orderId,
                shippingGroupId,
                shippingMethods,
                priceInfo: orderDetails?.priceInfo,
                currentShippingMethodId
            };
        } catch (err) {
            Sephora.logger.error('[GooglePay] checkout init failed', err);
            checkoutUtils.initOrderFailure(err);

            return undefined;
        }

        const paymentDataRequest = getPaymentDataRequest({
            transactionAmount,
            transactionCurrencyCode,
            shippingMethods,
            currentShippingMethodId: checkoutContext.currentShippingMethodId
        });

        return this.requestPaymentData(paymentDataRequest);
    }
};

export default GooglePayUtils;
